/**
 * Browser detection utilities for Intercom/support context.
 *
 * This module provides comprehensive detection capabilities for:
 * - Operating systems (macOS, Windows, Linux, iOS, Android)
 * - Device types (desktop, mobile, tablet)
 * - Browsers (Chrome, Edge, Firefox, Safari, Opera, Brave, Yandex)
 *
 * Designed for integration with Intercom to provide support team context.
 */

// Export all types
export type {
  OperatingSystem,
  DeviceType,
  BrowserName,
  BrowserInfo,
  EnvironmentContext,
} from "./types";

// Export OS detection functions
export {
  detectOS,
  isMacOS,
  isWindows,
  isLinux,
  isMobileOS,
  _detectOSInternal,
} from "./osDetection";

// Export device detection functions
export {
  detectDeviceType,
  isDesktop,
  isMobile,
  isTablet,
} from "./deviceDetection";

// Export browser detection functions
export {
  detectBrowserName,
  detectBrowser,
  isChrome,
  isEdgeBrowser,
  isFirefox,
  isSafari,
  isOpera,
  isBrave,
  isYandex,
  getBrowserVersion,
  getBrowserFullVersion,
} from "./browserDetection";

// Export user-friendly name utilities
export {
  getFriendlyOSName,
  getFriendlyDeviceName,
  getFriendlyBrowserName,
  getSupportDescription,
} from "./userFriendlyNames";

// Export debug utilities
export {
  debugLogger,
  enableDebugLogging,
  disableDebugLogging,
} from "./debugLogger";

// Import detection functions for context aggregation
import { detectOS } from "./osDetection";
import { detectDeviceType } from "./deviceDetection";
import { detectBrowser } from "./browserDetection";
import { debugLogger } from "./debugLogger";
import {
  getFriendlyOSName,
  getFriendlyDeviceName,
  getFriendlyBrowserName,
  getSupportDescription,
} from "./userFriendlyNames";
import type { EnvironmentContext } from "./types";

/**
 * Gets complete environment context for Intercom/support integration.
 * This is the main function to use when passing context to Intercom.
 * :return: Complete environment context with all detection results and user-friendly descriptions.
 */
export function getEnvironmentContext(): EnvironmentContext {
  const os = detectOS();
  const device = detectDeviceType();
  const browserInfo = detectBrowser();

  const context: EnvironmentContext = {
    browser: browserInfo.name,
    browserVersion: browserInfo.version,
    browserFullVersion: browserInfo.fullVersion,
    operatingSystem: os,
    deviceType: device,
    userAgent: browserInfo.userAgent,
    browserFriendly: getFriendlyBrowserName(browserInfo.name),
    osFriendly: getFriendlyOSName(os),
    deviceFriendly: getFriendlyDeviceName(device),
    supportDescription: getSupportDescription(
      os,
      device,
      browserInfo.name,
      browserInfo.version
    ),
  };

  debugLogger.logSummary({
    os,
    device,
    browser: browserInfo.name,
    browserVersion: browserInfo.version,
  });

  return context;
}

/**
 * Gets environment context formatted for Intercom custom attributes.
 * Returns only the fields that should be passed to Intercom.
 * :return: Object with Intercom-compatible attribute names and values.
 */
export function getIntercomAttributes(): Record<string, string | undefined> {
  const context = getEnvironmentContext();

  // Return attributes, using undefined instead of null (will be filtered out)
  // This prevents Intercom from showing "Unknown" for null values
  return {
    browser: context.browserFriendly || undefined,
    browserVersion: context.browserVersion || undefined,
    operatingSystem: context.osFriendly || undefined,
    deviceType: context.deviceFriendly || undefined,
    // Include technical values for filtering/searching
    browserTechnical: context.browser || undefined,
    osTechnical: context.operatingSystem || undefined,
    deviceTechnical: context.deviceType || undefined,
  };
}

/**
 * Performs all detection operations and logs a summary if debug is enabled.
 * Useful for debugging and verifying detection accuracy.
 * :return: Object containing all detection results.
 */
export function logDetectionSummary(): {
  os: string;
  device: string;
  browser: string;
  browserVersion: string | null;
} {
  const os = detectOS();
  const device = detectDeviceType();
  const browserInfo = detectBrowser();

  debugLogger.logSummary({
    os,
    device,
    browser: browserInfo.name,
    browserVersion: browserInfo.version,
  });

  return {
    os,
    device,
    browser: browserInfo.name,
    browserVersion: browserInfo.version,
  };
}
